import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    computed,
    ViewEncapsulation,
    OnInit,
    signal,
} from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IOnboardingListItem, IOnboardingParameter } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.service';
import { FiOnboardingActionsBuilderService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-actions-builder.service';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiCustomerDataService } from '@fi-app/shared/fi-shared-services/fi-customer-data.service';
import { FiOnboardingCompletionSyncService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-sync.service';

@Component({
    selector: 'fi-onboarding-list',
    templateUrl: './fi-onboarding-list.component.html',
    styleUrl: './fi-onboarding-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-list' },
    standalone: false,
})
export class FiOnboardingListComponent extends FiRemoteStateFullDataSource<IOnboardingListItem> implements OnInit {
    public onboardingIds = input<number[]>([]);
    public stepElementObjectIds = input<number[]>([]);
    public stepElementObjectTypes = input<ObjectTypes[]>([]);

    private readonly fiOnboardingService = inject(FiOnboardingService);
    private readonly fiOnboardingActionsBuilderService = inject(FiOnboardingActionsBuilderService);
    private readonly fiOnboardingHelperService = inject(FiOnboardingHelperService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);
    private readonly fiOnboardingCompletionSyncService = inject(FiOnboardingCompletionSyncService);

    public readonly defaultSortColumnName = 'CreatedOn';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.Onboarding;
    public readonly itemsListType = FiItemsListType.OnboardingListItems;

    protected readonly sideListOptions: ISideListOptionsItem[] =
        this.fiOnboardingHelperService.getOnboardingBuilderListSideListOptions();

    protected readonly actions = computed<IItemsListAction2<IOnboardingListItem>[]>(() => {
        return this.fiOnboardingActionsBuilderService.getOnboardingActions({
            onOnboardingDeletedFn: (item) => this.removeData([item]),
            stepElementObjectIds: this.stepElementObjectIds(),
            stepElementObjectTypes: this.stepElementObjectTypes(),
        });
    });
    protected readonly isSyncOnboardingsEnabled = this.fiCustomerDataService.isSynchronizedOnboardingEnabled();
    protected readonly OnboardingStatusesConfig = this.fiOnboardingHelperService.OnboardingStatusesConfig;

    protected getRowClassFn = (item: IOnboardingListItem) => (item.HasEditAccess ? 'cursor-pointer' : '');

    public ngOnInit(): void {
        this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData(undefined);
    }

    protected navigateToOnboardingViewer(item: IOnboardingListItem): void {
        this.fiStateService.go(this.FiStateNames.Onboarding.View, {
            id: item.Id,
            stepElementObjectIds: this.stepElementObjectIds(),
            stepElementObjectTypes: this.stepElementObjectTypes(),
        });
    }

    protected isEmptyListIsShown = signal(false);

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IOnboardingListItem>> {
        return new Promise<IItemsListDataFetchResult<IOnboardingListItem>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiOnboardingService.getOnboardingsListData(parameter).then((data) => {
                if (this.loadingCounter() === 1) {
                    this.isEmptyListIsShown.set(data.Items.length === 0);
                }
                resolve(data);
            }, reject);
        });
    }

    private getParameter(loadArgs: ILoadDataArgs): IItemsListParameterBase {
        const parameter = super.getItemsListParameter(loadArgs) as IOnboardingParameter;
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        parameter.Ids = this.onboardingIds();
        return parameter;
    }

    protected getCategoryText(item: IOnboardingListItem): string {
        return this.fiOnboardingHelperService.getCategoryText(item.Category);
    }

    protected getStatusText(IsActive: boolean): string {
        return this.fiOnboardingHelperService.getStatusText(IsActive);
    }

    protected getItemSharedStatusLabel(isShared: boolean): string {
        return this.fiOnboardingHelperService.getItemSharedStatusLabel(isShared);
    }

    protected readonly MatSortDirection = MatSortDirection;
}
